import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronRight, Instagram, Phone, Globe, ArrowRight, ChevronLeft } from "lucide-react";
import axios from "axios";
import { trackEvent, trackStandardEvent, FunnelEvents } from "../utils/tracking";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language options
const LANGUAGES = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'es', label: 'Español', nativeLabel: 'Español' },
  { code: 'fr', label: 'Français', nativeLabel: 'Français' },
  { code: 'de', label: 'Deutsch', nativeLabel: 'Deutsch' },
  { code: 'it', label: 'Italiano', nativeLabel: 'Italiano' },
  { code: 'pl', label: 'Polski', nativeLabel: 'Polski' },
  { code: 'nl', label: 'Nederlands', nativeLabel: 'Nederlands', flag: '🇧🇪' },
  { code: 'lt', label: 'Lietuvių', nativeLabel: 'Lietuvių' },
];

// Image URLs
const IMAGES = {
  heroBefore: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/c639529d086b10be_01-hero-before.jpg",
  heroAfter: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/3928d88e41ef8c17_02-hero-after.jpg",
  storyJuly: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/402d669a95f80633_03-story-july-2025.jpg",
  storyToday: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/755dac95f0d7accb_04-story-today.jpg",
  clientThinBefore: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/aa4cea46ca826047_05-client-thinning-before.jpg",
  clientThinAfter: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/1ec2ddacdcd916b2_06-client-thinning-after.jpg",
  clientCurlyBefore: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/1372f6d04957dcef_07-client-curly-before.jpg",
  clientCurlyAfter: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/e3e3304f867dccb5_08-client-curly-after.jpg",
  clientSilverBefore: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/e241bcec31c60ff5_09-client-silver-before.jpg",
  clientSilverAfter: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/46b2f26b94191662_10-client-silver-after.jpg",
  clientFineBefore: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/12a6b4697af1a931_11-client-fine-before.jpg",
  clientFineAfter: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/a77c331c26e7779e_12-client-fine-after.jpg",
  monatLogo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/1de25bfdc9ed430e_13-monat-drop-mark.png",
  // Products
  p1Serum: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/4c1e8e1b07ffee01_p1-hair-thinning-defense-serum.png",
  p2Oil: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/89fdd717f1b9b4cc_p2-rejuveniqe-oil.png",
  p6Rejuvabeads: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/219d317bb86d8688_p6-rejuvabeads.webp",
  // Customer DMs
  r1: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/8bf4112320d9c320_r1-dm-whole-head.jpg",
  r2: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/9aac5976ddeb3436_r2-dm-haircut.jpg",
  r3: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/659f2eba07b5dd79_r3-dm-love-it.jpg",
  r4: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/15a17ca8906996f7_r4-dm-mailbox.jpg",
  r5: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/c61899a90f7e649a_r5-dm-wall.jpg",
  // Flags
  flags18: "https://customer-assets-m6fa6gv7.emergentagent.net/job_20074a01-0522-4acf-a362-5ce15b30849a/artifacts/1ec034a668af67dc_f1-flags-18-countries.webp",
};

// TikTok and Meta SVG icons
const TikTokIcon = () => (
  <svg className="w-5 h-5 inline-block" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const MetaIcon = () => (
  <svg className="w-5 h-5 inline-block" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 008.44-9.9c0-5.53-4.5-10.02-10-10.02z"/>
  </svg>
);

// English content
const CONTENT_EN = {
  pageTitle: "find your hair routine · kristen hinman",
  langButton: "ver en español",
  headline: "modern nature for",
  headlineHighlight: "your hair",
  subline: "functional medicine for your scalp. plant-based, clinically studied luxury haircare, loved by 3 million+ customers worldwide. answer 3 quick questions and i'll personally build your hair routine.",
  beforeAfter: "BEFORE · AFTER",
  // What is MONAT section
  whatIsMonatEyebrow: "what is monat",
  whatIsMonatHeading: "luxury haircare that",
  whatIsMonatHighlight: "starts at the scalp",
  whatIsMonatBody: "monat is a family-owned haircare brand, 12 years strong, built on modern nature: plant-based ingredients and clinically studied formulas that nourish your scalp and hair from the root. no harsh sulfates or parabens. every formula passes EU standards.",
  stats: ["3m+ customers worldwide", "12 years", "passes EU standards", "18 countries"],
  // Women love it for
  womenLoveIt: "women love it for",
  tags: ["less shedding", "more density", "less frizz", "postpartum hair", "menopause hair", "hair loss after illness or stress", "stronger ends", "a calmer scalp", "+ more"],
  // Best Sellers section (NEW)
  bestSellersEyebrow: "our best sellers",
  bestSellersHeading: "the ones",
  bestSellersHighlight: "everyone asks me about",
  shopNow: "shop now",
  before: "before",
  after: "after",
  products: [
    { 
      emoji: "🧴‍♀️",
      name: "hair thinning defense serum", 
      desc: "my \"extensions in a bottle.\" a daily scalp serum that's clinically shown to reduce hair fall by 92% while boosting growth + density. it's water based, so it never makes your hair greasy.",
      tip: "my tip: keep it next to your toothbrush so you actually use it every day.",
      img: "p1Serum",
      link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=ir-clinical-hair-thinning-defense"
    },
    { 
      emoji: "💧",
      name: "rejuveniqe oil", 
      desc: "the one i'd never give up. 13 plant oils, antioxidants + nutrients that bring moisture, shine and softness back. a few drops on your ends or as a scalp treatment. vegan + cruelty free.",
      tip: "",
      img: "p2Oil",
      link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuveniqe-oil-intensive"
    },
    { 
      emoji: "🧜‍♀️",
      name: "rejuvabeads", 
      desc: "my split end mender. it mends split ends in 60 seconds so your ends stop breaking off and you keep your length. growing your hair and keeping your length are two different things, and this is the keeping part.",
      tip: "",
      img: "p6Rejuvabeads",
      link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuvabeads"
    },
  ],
  productsNotSure: "not sure which ones are right for you? that's exactly what the quiz is for.",
  productsCta: "find your hair routine",
  // My hair story
  storyEyebrow: "my hair story",
  storyHeading: "same spot,",
  storyHighlight: "a lot more hair",
  storyP1: "in july 2025 i was looking at my hair and the only words i had for it were",
  storyP1Bold: "stringy and see-through",
  storyP1End: ". thin at my temples, sparse at the ends.",
  storyP2: "i was honestly skeptical of trying monat. i tried it anyway, and i kept it simple:",
  storyP2Bold: "an oil and a hair thinning defense serum",
  storyP2End: ", every single day. (my tip: keep the serum next to your toothbrush so you actually use it.)",
  storyP3: "just over a year later, it's the same spot with a lot more hair. i knew my hair had changed. i didn't realize how much until i saw the photos side by side.",
  // Client results
  clientResults: "real client results",
  // Happy customers
  happyEyebrow: "happy customers",
  happyHeading: "my DMs",
  happyHighlight: "lately",
  happyNote: "real messages from customers, shared with permission.",
  // Slider hint
  sliderHint: "swipe to see more →",
  // How it works
  howItWorksHeading: "how it works",
  howItWorks: [
    { num: "1", title: "take the quiz", desc: "tell me what's going on with your hair. it takes about a minute." },
    { num: "2", title: "i build your routine", desc: "i personally message you the products i'd use if i were you, and why." },
    { num: "3", title: "delivered to your door", desc: "your routine ships straight to you, wherever you live." },
  ],
  howItWorksGuarantee: "30 days money-back guarantee, so you can try it risk-free.",
  whereWeShip: "available in 18 countries + growing",
  // Final CTA section
  readyHeading: "ready for your hair routine?",
  readyCta: "take the 1-minute quiz",
  // Quiz
  quizTitle: "find your hair routine",
  step1Title: "what's going on with your hair?",
  step1Subtitle: "select all that apply",
  step1Options: [
    "thinning or shedding",
    "postpartum hair loss",
    "menopause-related changes",
    "stress or illness hair loss",
    "frizzy or unmanageable",
    "dry or damaged ends",
    "oily or flaky scalp",
    "slow growth",
    "color-treated or bleached",
    "fine or flat hair",
    "curly or textured hair",
    "other"
  ],
  step2Title: "what does your current routine look like?",
  step2Subtitle: "select all that apply",
  step2Options: [
    "i wash daily",
    "i wash 2-3x per week",
    "i wash once a week or less",
    "i use heat tools often",
    "i air dry mostly",
    "i use drugstore products",
    "i've tried everything",
    "i don't really have a routine"
  ],
  step3Title: "almost there! where should i send your routine?",
  step3Subtitle: "i'll personally message you with your custom recommendations",
  firstNameLabel: "first name",
  firstNamePlaceholder: "your name",
  contactMethodLabel: "how should i reach you?",
  instagramOption: "instagram dm",
  textOption: "text message",
  igHandlePlaceholder: "@yourhandle",
  countryLabel: "country",
  countryPlaceholder: "select your country",
  phoneLabel: "mobile number",
  phonePlaceholder: "+1 555 123 4567",
  submitButton: "send my routine",
  submitting: "sending...",
  required: "required",
  thankYouTitle: "you're in!",
  thankYouMessage: "i read every one myself & i'll message you within 24 hours with the routine i'd use for your hair.",
  thankYouFollowLine: "if you picked instagram, follow me so my message doesn't get lost in your requests 👇",
  thankYouFollowBtn: "follow @iamkristenhinman",
  // Footer
  footerNew: "new",
  footerEarn: "earn extra money from your phone",
  footerShare: ", simply by sharing products you love. our affiliate program just launched worldwide on",
  footerTiktok: "tiktok",
  footerPlus: "+",
  footerMeta: "meta",
  footerRunIt: "want a better discount on your products and access to our affiliate program? start with my RUN IT program. DM me on instagram to find out how it works.",
  footerRunItBtn: "see how RUN IT works",
  footerDmBtn: "DM me on instagram",
  available: "available in 18 countries and growing",
  follow: "follow me on instagram @iamkristenhinman",
  errors: {
    firstName: "please enter your name",
    contact: "please enter your contact info",
    country: "please select your country",
    phone: "please enter your phone number"
  }
};

// Spanish content
const CONTENT_ES = {
  pageTitle: "encuentra tu rutina capilar · kristen hinman",
  langButton: "view in english",
  headline: "naturaleza moderna para",
  headlineHighlight: "tu pelo",
  subline: "medicina funcional para tu cuero cabelludo. cuidado capilar de lujo, de origen vegetal y clínicamente estudiado, querido por más de 3 millones de clientas en todo el mundo. responde 3 preguntas rápidas y te crearé tu rutina capilar personalmente.",
  beforeAfter: "ANTES · DESPUÉS",
  // What is MONAT section
  whatIsMonatEyebrow: "qué es monat",
  whatIsMonatHeading: "cuidado capilar de lujo que",
  whatIsMonatHighlight: "empieza en el cuero cabelludo",
  whatIsMonatBody: "monat es una marca familiar de cuidado capilar, con 12 años de trayectoria, construida sobre la naturaleza moderna: ingredientes de origen vegetal y fórmulas clínicamente estudiadas que nutren tu cuero cabelludo y pelo desde la raíz. sin sulfatos ni parabenos agresivos. cada fórmula cumple los estándares de la UE.",
  stats: ["3m+ clientas en todo el mundo", "12 años", "cumple estándares UE", "18 países"],
  // Women love it for
  womenLoveIt: "a las mujeres les encanta para",
  tags: ["menos caída", "más densidad", "menos frizz", "pelo tras el parto", "pelo en la menopausia", "caída tras enfermedad o estrés", "puntas más fuertes", "un cuero cabelludo en calma", "+ más"],
  // Best Sellers section (NEW)
  bestSellersEyebrow: "nuestros más vendidos",
  bestSellersHeading: "los que",
  bestSellersHighlight: "todo el mundo me pregunta",
  shopNow: "comprar ahora",
  before: "antes",
  after: "después",
  products: [
    { 
      emoji: "🧴‍♀️",
      name: "hair thinning defense serum", 
      desc: "mis \"extensiones en un bote.\" un sérum diario para el cuero cabelludo que está clínicamente demostrado que reduce la caída del pelo en un 92% mientras potencia el crecimiento y la densidad. es de base de agua, así que nunca deja el pelo graso.",
      tip: "mi consejo: ponlo al lado del cepillo de dientes para que lo uses de verdad todos los días.",
      img: "p1Serum",
      link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=ir-clinical-hair-thinning-defense"
    },
    { 
      emoji: "💧",
      name: "rejuveniqe oil", 
      desc: "el que nunca dejaría. 13 aceites vegetales, antioxidantes y nutrientes que devuelven hidratación, brillo y suavidad. unas gotas en las puntas o como tratamiento para el cuero cabelludo. vegano + cruelty free.",
      tip: "",
      img: "p2Oil",
      link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuveniqe-oil-intensive"
    },
    { 
      emoji: "🧜‍♀️",
      name: "rejuvabeads", 
      desc: "mi reparador de puntas abiertas. repara las puntas abiertas en 60 segundos para que dejen de romperse y mantengas tu longitud. hacer crecer el pelo y mantener la longitud son dos cosas diferentes, y esto es la parte de mantener.",
      tip: "",
      img: "p6Rejuvabeads",
      link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuvabeads"
    },
  ],
  productsNotSure: "¿no sabes cuáles son los adecuados para ti? para eso está el quiz.",
  productsCta: "encuentra tu rutina capilar",
  // My hair story
  storyEyebrow: "mi historia capilar",
  storyHeading: "el mismo sitio,",
  storyHighlight: "mucho más pelo",
  storyP1: "en julio de 2025 miraba mi pelo y las únicas palabras que tenía para él eran",
  storyP1Bold: "fibroso y transparente",
  storyP1End: ". fino en las sienes, escaso en las puntas.",
  storyP2: "sinceramente, era escéptica con probar monat. lo probé de todos modos, y lo mantuve simple:",
  storyP2Bold: "un aceite y un sérum defensa anticaída",
  storyP2End: ", todos los días. (mi consejo: pon el sérum al lado del cepillo de dientes para que lo uses de verdad.)",
  storyP3: "poco más de un año después, es el mismo sitio con mucho más pelo. sabía que mi pelo había cambiado. no me di cuenta de cuánto hasta que vi las fotos lado a lado.",
  // Client results
  clientResults: "resultados reales de clientas",
  // Happy customers
  happyEyebrow: "clientas felices",
  happyHeading: "mis DMs",
  happyHighlight: "últimamente",
  happyNote: "mensajes reales de clientas, compartidos con permiso.",
  // Slider hint
  sliderHint: "desliza para ver más →",
  // How it works
  howItWorksHeading: "cómo funciona",
  howItWorks: [
    { num: "1", title: "haz el quiz", desc: "cuéntame qué le pasa a tu pelo. tarda un minuto." },
    { num: "2", title: "te creo tu rutina", desc: "te escribo personalmente los productos que usaría si fuera tú, y por qué." },
    { num: "3", title: "a tu puerta", desc: "tu rutina se envía directamente a ti, vivas donde vivas." },
  ],
  howItWorksGuarantee: "garantía de devolución de 30 días, para que puedas probarlo sin riesgo.",
  whereWeShip: "disponible en 18 países + creciendo",
  // Final CTA section
  readyHeading: "¿lista para tu rutina capilar?",
  readyCta: "haz el quiz de 1 minuto",
  // Quiz
  quizTitle: "encuentra tu rutina capilar",
  step1Title: "¿qué le pasa a tu pelo?",
  step1Subtitle: "selecciona todo lo que aplique",
  step1Options: [
    "pérdida o caída",
    "caída postparto",
    "cambios por la menopausia",
    "caída por estrés o enfermedad",
    "encrespado o inmanejable",
    "puntas secas o dañadas",
    "cuero cabelludo graso o con caspa",
    "crecimiento lento",
    "teñido o decolorado",
    "pelo fino o sin volumen",
    "pelo rizado o con textura",
    "otro"
  ],
  step2Title: "¿cómo es tu rutina actual?",
  step2Subtitle: "selecciona todo lo que aplique",
  step2Options: [
    "me lavo a diario",
    "me lavo 2-3 veces por semana",
    "me lavo una vez a la semana o menos",
    "uso secador o plancha a menudo",
    "casi siempre dejo secar al aire",
    "uso productos de supermercado",
    "ya lo he probado todo",
    "realmente no tengo rutina"
  ],
  step3Title: "¡ya casi! ¿dónde te envío tu rutina?",
  step3Subtitle: "te escribiré personalmente con tus recomendaciones",
  firstNameLabel: "nombre",
  firstNamePlaceholder: "tu nombre",
  contactMethodLabel: "¿cómo te contacto?",
  instagramOption: "dm de instagram",
  textOption: "mensaje de texto",
  igHandlePlaceholder: "@tuusuario",
  countryLabel: "país",
  countryPlaceholder: "selecciona tu país",
  phoneLabel: "número de móvil",
  phonePlaceholder: "+34 612 345 678",
  submitButton: "enviar mi rutina",
  submitting: "enviando...",
  required: "obligatorio",
  thankYouTitle: "¡estás dentro!",
  thankYouMessage: "leo cada uno personalmente y te escribiré en las próximas 24 horas con la rutina que usaría para tu cabello.",
  thankYouFollowLine: "si elegiste instagram, sígueme para que mi mensaje no se pierda en tus solicitudes 👇",
  thankYouFollowBtn: "seguir a @iamkristenhinman",
  // Footer
  footerNew: "nuevo",
  footerEarn: "gana dinero extra desde tu móvil",
  footerShare: ", simplemente compartiendo productos que te encantan. nuestro programa de afiliados acaba de lanzarse en todo el mundo en",
  footerTiktok: "tiktok",
  footerPlus: "+",
  footerMeta: "meta",
  footerRunIt: "¿quieres un mejor descuento en tus productos y acceso a nuestro programa de afiliados? empieza con mi programa RUN IT. escríbeme por instagram para saber cómo funciona.",
  footerRunItBtn: "ver cómo funciona RUN IT",
  footerDmBtn: "escríbeme en instagram",
  available: "disponible en 18 países y creciendo",
  follow: "sígueme en instagram @iamkristenhinman",
  errors: {
    firstName: "por favor escribe tu nombre",
    contact: "por favor escribe tu información de contacto",
    country: "por favor selecciona tu país",
    phone: "por favor escribe tu número de teléfono"
  }
};

// French content
const CONTENT_FR = {
  pageTitle: "trouve ta routine capillaire · kristen hinman",
  headline: "la nature moderne pour",
  headlineHighlight: "tes cheveux",
  subline: "médecine fonctionnelle pour ton cuir chevelu. soins capillaires de luxe à base de plantes, cliniquement étudiés, adorés par plus de 3 millions de clientes dans le monde. réponds à 3 questions rapides et je te crée personnellement ta routine capillaire.",
  beforeAfter: "AVANT · APRÈS",
  before: "avant",
  after: "après",
  whatIsMonatEyebrow: "qu'est-ce que monat",
  whatIsMonatHeading: "soins capillaires de luxe qui",
  whatIsMonatHighlight: "commencent au cuir chevelu",
  whatIsMonatBody: "monat est une marque familiale de soins capillaires, forte de 12 ans d'expérience, construite sur la nature moderne : des ingrédients à base de plantes et des formules cliniquement étudiées qui nourrissent ton cuir chevelu et tes cheveux depuis la racine. sans sulfates ni parabènes agressifs. chaque formule respecte les normes européennes.",
  stats: ["3m+ clientes dans le monde", "12 ans", "normes UE", "18 pays"],
  womenLoveIt: "les femmes l'adorent pour",
  tags: ["moins de chute", "plus de densité", "moins de frisottis", "cheveux post-partum", "cheveux ménopause", "chute après maladie ou stress", "pointes plus fortes", "un cuir chevelu apaisé", "+ plus"],
  bestSellersEyebrow: "nos best-sellers",
  bestSellersHeading: "ceux que",
  bestSellersHighlight: "tout le monde me demande",
  shopNow: "acheter",
  products: [
    { emoji: "🧴", name: "hair thinning defense serum", desc: "mes \"extensions en flacon.\" un sérum quotidien pour le cuir chevelu cliniquement prouvé pour réduire la chute de 92% tout en stimulant la croissance et la densité. à base d'eau, jamais gras.", tip: "mon conseil : pose-le à côté de ta brosse à dents pour l'utiliser vraiment tous les jours.", img: "p1Serum", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=ir-clinical-hair-thinning-defense" },
    { emoji: "💧", name: "rejuveniqe oil", desc: "celui dont je ne me passerais jamais. 13 huiles végétales, antioxydants et nutriments qui redonnent hydratation, brillance et douceur. quelques gouttes sur les pointes ou en traitement cuir chevelu. vegan + cruelty free.", tip: "", img: "p2Oil", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuveniqe-oil-intensive" },
    { emoji: "🧜", name: "rejuvabeads", desc: "mon réparateur de pointes fourchues. il répare les pointes fourchues en 60 secondes pour qu'elles arrêtent de se casser et que tu gardes ta longueur. faire pousser ses cheveux et garder sa longueur sont deux choses différentes, et ça c'est la partie pour garder.", tip: "", img: "p6Rejuvabeads", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuvabeads" },
  ],
  productsNotSure: "pas sûre de ce qui te convient ? c'est exactement pour ça qu'il y a le quiz.",
  productsCta: "trouve ta routine capillaire",
  storyEyebrow: "mon histoire capillaire",
  storyHeading: "le même endroit,",
  storyHighlight: "beaucoup plus de cheveux",
  storyP1: "en juillet 2025 je regardais mes cheveux et les seuls mots que j'avais pour eux étaient",
  storyP1Bold: "filasses et transparents",
  storyP1End: ". fins aux tempes, clairsemés aux pointes.",
  storyP2: "honnêtement, j'étais sceptique d'essayer monat. je l'ai essayé quand même, et j'ai gardé ça simple :",
  storyP2Bold: "une huile et un sérum défense anti-chute",
  storyP2End: ", tous les jours. (mon conseil : pose le sérum à côté de ta brosse à dents pour l'utiliser vraiment.)",
  storyP3: "un peu plus d'un an après, c'est le même endroit avec beaucoup plus de cheveux. je savais que mes cheveux avaient changé. je ne me rendais pas compte à quel point jusqu'à voir les photos côte à côte.",
  clientResults: "vrais résultats de clientes",
  happyEyebrow: "clientes heureuses",
  happyHeading: "mes DMs",
  happyHighlight: "dernièrement",
  happyNote: "vrais messages de clientes, partagés avec permission.",
  sliderHint: "glisse pour voir plus →",
  howItWorksHeading: "comment ça marche",
  howItWorks: [
    { num: "1", title: "fais le quiz", desc: "dis-moi ce qui se passe avec tes cheveux. ça prend environ une minute." },
    { num: "2", title: "je crée ta routine", desc: "je t'envoie personnellement les produits que j'utiliserais si j'étais toi, et pourquoi." },
    { num: "3", title: "livré chez toi", desc: "ta routine est envoyée directement chez toi, où que tu habites." },
  ],
  howItWorksGuarantee: "garantie satisfait ou remboursé 30 jours, pour essayer sans risque.",
  whereWeShip: "disponible dans 18 pays + en croissance",
  quizTitle: "trouve ta routine capillaire",
  step1Title: "que se passe-t-il avec tes cheveux ?",
  step1Subtitle: "sélectionne tout ce qui s'applique",
  step1Options: ["chute ou perte", "chute post-partum", "changements liés à la ménopause", "chute après stress ou maladie", "frisottis ou indomptables", "pointes sèches ou abîmées", "cuir chevelu gras ou pelliculeux", "croissance lente", "colorés ou décolorés", "fins ou plats", "bouclés ou texturés", "autre"],
  step2Title: "à quoi ressemble ta routine actuelle ?",
  step2Subtitle: "sélectionne tout ce qui s'applique",
  step2Options: ["je lave tous les jours", "je lave 2-3x par semaine", "je lave une fois par semaine ou moins", "j'utilise souvent des outils chauffants", "je sèche à l'air la plupart du temps", "j'utilise des produits de supermarché", "j'ai tout essayé", "je n'ai pas vraiment de routine"],
  step3Title: "presque fini ! où t'envoyer ta routine ?",
  step3Subtitle: "je t'enverrai personnellement mes recommandations",
  firstNameLabel: "prénom",
  firstNamePlaceholder: "ton prénom",
  contactMethodLabel: "comment te contacter ?",
  instagramOption: "dm instagram",
  textOption: "sms",
  igHandlePlaceholder: "@tonpseudo",
  countryLabel: "pays",
  countryPlaceholder: "sélectionne ton pays",
  phoneLabel: "numéro de mobile",
  phonePlaceholder: "+33 6 12 34 56 78",
  submitButton: "envoyer ma routine",
  submitting: "envoi en cours...",
  thankYouTitle: "c'est fait !",
  thankYouMessage: "je lis chacun personnellement et je t'enverrai dans les 24 heures la routine que j'utiliserais pour tes cheveux.",
  thankYouFollowLine: "si tu as choisi instagram, suis-moi pour que mon message ne se perde pas dans tes demandes 👇",
  thankYouFollowBtn: "suivre @iamkristenhinman",
  footerNew: "nouveau",
  footerEarn: "gagne de l'argent depuis ton téléphone",
  footerShare: ", simplement en partageant des produits que tu adores. notre programme d'affiliation vient de se lancer dans le monde entier sur",
  footerTiktok: "tiktok",
  footerPlus: "+",
  footerMeta: "meta",
  footerRunIt: "tu veux une meilleure réduction sur tes produits et accéder à notre programme d'affiliation ? commence avec mon programme RUN IT. écris-moi sur instagram pour savoir comment ça marche.",
  footerRunItBtn: "voir comment RUN IT fonctionne",
  footerDmBtn: "écris-moi sur instagram",
  available: "disponible dans 18 pays et en croissance",
  follow: "suis-moi sur instagram @iamkristenhinman",
  errors: { firstName: "entre ton prénom", contact: "entre tes coordonnées", country: "sélectionne ton pays", phone: "entre ton numéro de téléphone" }
};

// German content
const CONTENT_DE = {
  pageTitle: "finde deine haarpflege-routine · kristen hinman",
  headline: "moderne natur für",
  headlineHighlight: "dein haar",
  subline: "funktionelle medizin für deine kopfhaut. pflanzenbasierte, klinisch getestete luxus-haarpflege, geliebt von über 3 millionen kundinnen weltweit. beantworte 3 kurze fragen und ich erstelle persönlich deine haar-routine.",
  beforeAfter: "VORHER · NACHHER",
  before: "vorher",
  after: "nachher",
  whatIsMonatEyebrow: "was ist monat",
  whatIsMonatHeading: "luxus-haarpflege, die",
  whatIsMonatHighlight: "an der kopfhaut beginnt",
  whatIsMonatBody: "monat ist eine familiengeführte haarpflegemarke, seit 12 jahren stark, aufgebaut auf moderner natur: pflanzenbasierte inhaltsstoffe und klinisch getestete formeln, die deine kopfhaut und dein haar von der wurzel an nähren. keine aggressiven sulfate oder parabene. jede formel erfüllt EU-standards.",
  stats: ["3m+ kundinnen weltweit", "12 jahre", "EU-standards", "18 länder"],
  womenLoveIt: "frauen lieben es für",
  tags: ["weniger haarausfall", "mehr dichte", "weniger frizz", "haare nach der geburt", "haare in der menopause", "haarausfall nach krankheit oder stress", "stärkere spitzen", "beruhigte kopfhaut", "+ mehr"],
  bestSellersEyebrow: "unsere bestseller",
  bestSellersHeading: "die produkte, nach denen",
  bestSellersHighlight: "mich alle fragen",
  shopNow: "jetzt kaufen",
  products: [
    { emoji: "🧴", name: "hair thinning defense serum", desc: "meine \"extensions aus der flasche.\" ein tägliches kopfhautserum, klinisch nachgewiesen haarausfall um 92% zu reduzieren und gleichzeitig wachstum + dichte zu fördern. wasserbasiert, nie fettig.", tip: "mein tipp: stell es neben deine zahnbürste, damit du es wirklich jeden tag benutzt.", img: "p1Serum", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=ir-clinical-hair-thinning-defense" },
    { emoji: "💧", name: "rejuveniqe oil", desc: "das produkt, auf das ich nie verzichten würde. 13 pflanzenöle, antioxidantien + nährstoffe, die feuchtigkeit, glanz und geschmeidigkeit zurückbringen. ein paar tropfen auf die spitzen oder als kopfhautbehandlung. vegan + tierversuchsfrei.", tip: "", img: "p2Oil", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuveniqe-oil-intensive" },
    { emoji: "🧜", name: "rejuvabeads", desc: "mein spliss-retter. repariert spliss in 60 sekunden, damit deine spitzen aufhören abzubrechen und du deine länge behältst. haare wachsen lassen und länge behalten sind zwei verschiedene dinge, und das ist der behalten-teil.", tip: "", img: "p6Rejuvabeads", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuvabeads" },
  ],
  productsNotSure: "nicht sicher, was für dich passt? genau dafür ist das quiz da.",
  productsCta: "finde deine haar-routine",
  storyEyebrow: "meine haar-geschichte",
  storyHeading: "dieselbe stelle,",
  storyHighlight: "viel mehr haar",
  storyP1: "im juli 2025 schaute ich mir mein haar an und die einzigen worte, die ich dafür hatte, waren",
  storyP1Bold: "strähnig und durchsichtig",
  storyP1End: ". dünn an den schläfen, spärlich an den spitzen.",
  storyP2: "ehrlich gesagt war ich skeptisch, monat auszuprobieren. ich habe es trotzdem versucht und es einfach gehalten:",
  storyP2Bold: "ein öl und ein haarausfall-defense-serum",
  storyP2End: ", jeden einzelnen tag. (mein tipp: stell das serum neben deine zahnbürste, damit du es wirklich benutzt.)",
  storyP3: "etwas mehr als ein jahr später ist es dieselbe stelle mit viel mehr haar. ich wusste, dass sich mein haar verändert hatte. ich habe nicht gemerkt, wie sehr, bis ich die fotos nebeneinander sah.",
  clientResults: "echte kundinnen-ergebnisse",
  happyEyebrow: "zufriedene kundinnen",
  happyHeading: "meine DMs",
  happyHighlight: "zuletzt",
  happyNote: "echte nachrichten von kundinnen, mit erlaubnis geteilt.",
  sliderHint: "wische für mehr →",
  howItWorksHeading: "so funktioniert's",
  howItWorks: [
    { num: "1", title: "mach das quiz", desc: "erzähl mir, was mit deinem haar los ist. dauert etwa eine minute." },
    { num: "2", title: "ich erstelle deine routine", desc: "ich schicke dir persönlich die produkte, die ich an deiner stelle verwenden würde, und warum." },
    { num: "3", title: "zu dir nach hause geliefert", desc: "deine routine wird direkt zu dir geschickt, egal wo du wohnst." },
  ],
  howItWorksGuarantee: "30 tage geld-zurück-garantie, damit du risikofrei testen kannst.",
  whereWeShip: "verfügbar in 18 ländern + wachsend",
  quizTitle: "finde deine haar-routine",
  step1Title: "was ist mit deinem haar los?",
  step1Subtitle: "wähle alles aus, was zutrifft",
  step1Options: ["ausdünnung oder haarausfall", "haarausfall nach der geburt", "veränderungen durch die menopause", "haarausfall durch stress oder krankheit", "kraus oder widerspenstig", "trockene oder geschädigte spitzen", "fettige oder schuppige kopfhaut", "langsames wachstum", "gefärbt oder gebleicht", "fein oder platt", "lockig oder texturiert", "anderes"],
  step2Title: "wie sieht deine aktuelle routine aus?",
  step2Subtitle: "wähle alles aus, was zutrifft",
  step2Options: ["ich wasche täglich", "ich wasche 2-3x pro woche", "ich wasche einmal pro woche oder seltener", "ich benutze oft hitze-tools", "ich lasse meist lufttrocknen", "ich benutze drogerie-produkte", "ich habe alles probiert", "ich habe eigentlich keine routine"],
  step3Title: "fast geschafft! wohin soll ich deine routine schicken?",
  step3Subtitle: "ich schicke dir persönlich meine empfehlungen",
  firstNameLabel: "vorname",
  firstNamePlaceholder: "dein name",
  contactMethodLabel: "wie soll ich dich erreichen?",
  instagramOption: "instagram dm",
  textOption: "sms",
  igHandlePlaceholder: "@deinname",
  countryLabel: "land",
  countryPlaceholder: "wähle dein land",
  phoneLabel: "handynummer",
  phonePlaceholder: "+49 151 12345678",
  submitButton: "routine senden",
  submitting: "wird gesendet...",
  thankYouTitle: "du bist dabei!",
  thankYouMessage: "ich lese jeden persönlich und schicke dir innerhalb von 24 stunden die routine, die ich für dein haar verwenden würde.",
  thankYouFollowLine: "wenn du instagram gewählt hast, folge mir, damit meine nachricht nicht in deinen anfragen verloren geht 👇",
  thankYouFollowBtn: "folge @iamkristenhinman",
  footerNew: "neu",
  footerEarn: "verdiene geld von deinem handy aus",
  footerShare: ", einfach indem du produkte teilst, die du liebst. unser affiliate-programm ist gerade weltweit gestartet auf",
  footerTiktok: "tiktok",
  footerPlus: "+",
  footerMeta: "meta",
  footerRunIt: "willst du bessere rabatte auf deine produkte und zugang zu unserem affiliate-programm? starte mit meinem RUN IT programm. schreib mir auf instagram, um zu erfahren, wie es funktioniert.",
  footerRunItBtn: "sieh wie RUN IT funktioniert",
  footerDmBtn: "schreib mir auf instagram",
  available: "verfügbar in 18 ländern und wachsend",
  follow: "folge mir auf instagram @iamkristenhinman",
  errors: { firstName: "bitte gib deinen namen ein", contact: "bitte gib deine kontaktdaten ein", country: "bitte wähle dein land", phone: "bitte gib deine telefonnummer ein" }
};

// Italian content
const CONTENT_IT = {
  pageTitle: "trova la tua routine per capelli · kristen hinman",
  headline: "natura moderna per i",
  headlineHighlight: "tuoi capelli",
  subline: "medicina funzionale per il cuoio capelluto. cura dei capelli di lusso a base vegetale, clinicamente studiata, amata da oltre 3 milioni di clienti in tutto il mondo. rispondi a 3 domande veloci e ti creerò personalmente la tua routine per capelli.",
  beforeAfter: "PRIMA · DOPO",
  before: "prima",
  after: "dopo",
  whatIsMonatEyebrow: "cos'è monat",
  whatIsMonatHeading: "cura dei capelli di lusso che",
  whatIsMonatHighlight: "parte dal cuoio capelluto",
  whatIsMonatBody: "monat è un marchio familiare di cura dei capelli, forte di 12 anni, costruito sulla natura moderna: ingredienti a base vegetale e formule clinicamente studiate che nutrono il cuoio capelluto e i capelli dalla radice. senza solfati o parabeni aggressivi. ogni formula rispetta gli standard UE.",
  stats: ["3m+ clienti nel mondo", "12 anni", "standard UE", "18 paesi"],
  womenLoveIt: "le donne lo adorano per",
  tags: ["meno caduta", "più densità", "meno crespo", "capelli post-parto", "capelli in menopausa", "caduta dopo malattia o stress", "punte più forti", "cuoio capelluto calmato", "+ altro"],
  bestSellersEyebrow: "i nostri bestseller",
  bestSellersHeading: "quelli che",
  bestSellersHighlight: "tutti mi chiedono",
  shopNow: "acquista ora",
  products: [
    { emoji: "🧴", name: "hair thinning defense serum", desc: "le mie \"extension in bottiglia.\" un siero quotidiano per cuoio capelluto clinicamente dimostrato che riduce la caduta del 92% mentre stimola crescita + densità. a base d'acqua, mai unto.", tip: "il mio consiglio: mettilo vicino allo spazzolino così lo usi davvero ogni giorno.", img: "p1Serum", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=ir-clinical-hair-thinning-defense" },
    { emoji: "💧", name: "rejuveniqe oil", desc: "quello a cui non rinuncerei mai. 13 oli vegetali, antiossidanti + nutrienti che riportano idratazione, lucentezza e morbidezza. poche gocce sulle punte o come trattamento cuoio capelluto. vegan + cruelty free.", tip: "", img: "p2Oil", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuveniqe-oil-intensive" },
    { emoji: "🧜", name: "rejuvabeads", desc: "il mio riparatore di doppie punte. ripara le doppie punte in 60 secondi così smettono di spezzarsi e mantieni la lunghezza. far crescere i capelli e mantenere la lunghezza sono due cose diverse, e questo è la parte del mantenere.", tip: "", img: "p6Rejuvabeads", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuvabeads" },
  ],
  productsNotSure: "non sai quali sono giusti per te? è esattamente per questo che c'è il quiz.",
  productsCta: "trova la tua routine capelli",
  storyEyebrow: "la mia storia capelli",
  storyHeading: "stesso punto,",
  storyHighlight: "molti più capelli",
  storyP1: "a luglio 2025 guardavo i miei capelli e le uniche parole che avevo per loro erano",
  storyP1Bold: "filiformi e trasparenti",
  storyP1End: ". sottili alle tempie, radi alle punte.",
  storyP2: "onestamente, ero scettica nel provare monat. l'ho provato comunque, e l'ho tenuto semplice:",
  storyP2Bold: "un olio e un siero difesa anticaduta",
  storyP2End: ", ogni singolo giorno. (il mio consiglio: metti il siero vicino allo spazzolino così lo usi davvero.)",
  storyP3: "poco più di un anno dopo, è lo stesso punto con molti più capelli. sapevo che i miei capelli erano cambiati. non mi rendevo conto di quanto fino a vedere le foto affiancate.",
  clientResults: "risultati reali di clienti",
  happyEyebrow: "clienti felici",
  happyHeading: "i miei DMs",
  happyHighlight: "ultimamente",
  happyNote: "messaggi reali di clienti, condivisi con permesso.",
  sliderHint: "scorri per vedere di più →",
  howItWorksHeading: "come funziona",
  howItWorks: [
    { num: "1", title: "fai il quiz", desc: "dimmi cosa sta succedendo con i tuoi capelli. ci vuole circa un minuto." },
    { num: "2", title: "creo la tua routine", desc: "ti scrivo personalmente i prodotti che userei se fossi te, e perché." },
    { num: "3", title: "consegnato a casa tua", desc: "la tua routine viene spedita direttamente a te, ovunque tu viva." },
  ],
  howItWorksGuarantee: "garanzia soddisfatti o rimborsati 30 giorni, per provare senza rischi.",
  whereWeShip: "disponibile in 18 paesi + in crescita",
  quizTitle: "trova la tua routine capelli",
  step1Title: "cosa sta succedendo con i tuoi capelli?",
  step1Subtitle: "seleziona tutto ciò che si applica",
  step1Options: ["diradamento o caduta", "caduta post-parto", "cambiamenti della menopausa", "caduta da stress o malattia", "crespi o ingestibili", "punte secche o danneggiate", "cuoio capelluto grasso o con forfora", "crescita lenta", "colorati o decolorati", "fini o piatti", "ricci o texturizzati", "altro"],
  step2Title: "com'è la tua routine attuale?",
  step2Subtitle: "seleziona tutto ciò che si applica",
  step2Options: ["lavo ogni giorno", "lavo 2-3x a settimana", "lavo una volta a settimana o meno", "uso spesso strumenti termici", "asciugo all'aria per lo più", "uso prodotti da supermercato", "ho provato di tutto", "non ho davvero una routine"],
  step3Title: "quasi finito! dove ti mando la tua routine?",
  step3Subtitle: "ti scriverò personalmente con le mie raccomandazioni",
  firstNameLabel: "nome",
  firstNamePlaceholder: "il tuo nome",
  contactMethodLabel: "come ti contatto?",
  instagramOption: "dm instagram",
  textOption: "sms",
  igHandlePlaceholder: "@iltuonome",
  countryLabel: "paese",
  countryPlaceholder: "seleziona il tuo paese",
  phoneLabel: "numero di cellulare",
  phonePlaceholder: "+39 333 1234567",
  submitButton: "invia la mia routine",
  submitting: "invio in corso...",
  thankYouTitle: "ci sei!",
  thankYouMessage: "leggo ogni messaggio personalmente e ti scriverò entro 24 ore con la routine che userei per i tuoi capelli.",
  thankYouFollowLine: "se hai scelto instagram, seguimi così il mio messaggio non si perde nelle tue richieste 👇",
  thankYouFollowBtn: "segui @iamkristenhinman",
  footerNew: "nuovo",
  footerEarn: "guadagna dal tuo telefono",
  footerShare: ", semplicemente condividendo prodotti che ami. il nostro programma affiliati è appena partito in tutto il mondo su",
  footerTiktok: "tiktok",
  footerPlus: "+",
  footerMeta: "meta",
  footerRunIt: "vuoi uno sconto migliore sui tuoi prodotti e accesso al nostro programma affiliati? inizia con il mio programma RUN IT. scrivimi su instagram per sapere come funziona.",
  footerRunItBtn: "scopri come funziona RUN IT",
  footerDmBtn: "scrivimi su instagram",
  available: "disponibile in 18 paesi e in crescita",
  follow: "seguimi su instagram @iamkristenhinman",
  errors: { firstName: "inserisci il tuo nome", contact: "inserisci i tuoi contatti", country: "seleziona il tuo paese", phone: "inserisci il tuo numero di telefono" }
};

// Polish content
const CONTENT_PL = {
  pageTitle: "znajdź swoją rutynę pielęgnacji włosów · kristen hinman",
  headline: "nowoczesna natura dla",
  headlineHighlight: "twoich włosów",
  subline: "medycyna funkcjonalna dla twojej skóry głowy. luksusowa pielęgnacja włosów na bazie roślinnej, klinicznie przebadana, kochana przez ponad 3 miliony klientek na całym świecie. odpowiedz na 3 szybkie pytania, a osobiście stworzę dla ciebie rutynę pielęgnacji włosów.",
  beforeAfter: "PRZED · PO",
  before: "przed",
  after: "po",
  whatIsMonatEyebrow: "czym jest monat",
  whatIsMonatHeading: "luksusowa pielęgnacja włosów, która",
  whatIsMonatHighlight: "zaczyna się od skóry głowy",
  whatIsMonatBody: "monat to rodzinna marka pielęgnacji włosów, z 12-letnim doświadczeniem, zbudowana na nowoczesnej naturze: składniki roślinne i klinicznie przebadane formuły, które odżywiają skórę głowy i włosy od samego korzenia. bez agresywnych siarczanów i parabenów. każda formuła spełnia standardy UE.",
  stats: ["3m+ klientek na świecie", "12 lat", "standardy UE", "18 krajów"],
  womenLoveIt: "kobiety kochają to za",
  tags: ["mniej wypadania", "więcej gęstości", "mniej puszenia się", "włosy po ciąży", "włosy w menopauzie", "wypadanie po chorobie lub stresie", "mocniejsze końcówki", "uspokojona skóra głowy", "+ więcej"],
  bestSellersEyebrow: "nasze bestsellery",
  bestSellersHeading: "produkty, o które",
  bestSellersHighlight: "wszyscy mnie pytają",
  shopNow: "kup teraz",
  products: [
    { emoji: "🧴", name: "hair thinning defense serum", desc: "moje \"przedłużki w butelce.\" codzienne serum do skóry głowy, klinicznie udowodnione, że zmniejsza wypadanie włosów o 92% jednocześnie stymulując wzrost + gęstość. na bazie wody, nigdy nie pozostawia włosów tłustych.", tip: "moja rada: postaw je obok szczoteczki do zębów, żebyś naprawdę używała go codziennie.", img: "p1Serum", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=ir-clinical-hair-thinning-defense" },
    { emoji: "💧", name: "rejuveniqe oil", desc: "ten, bez którego nigdy bym się nie obeszła. 13 olejków roślinnych, przeciwutleniacze + składniki odżywcze, które przywracają nawilżenie, blask i miękkość. kilka kropli na końcówki lub jako kuracja na skórę głowy. wegański + cruelty free.", tip: "", img: "p2Oil", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuveniqe-oil-intensive" },
    { emoji: "🧜", name: "rejuvabeads", desc: "mój ratownik rozdwojonych końcówek. naprawia rozdwojone końcówki w 60 sekund, żeby przestały się łamać i zachowałaś długość. zapuszczanie włosów i utrzymanie długości to dwie różne rzeczy, a to jest ta część utrzymania.", tip: "", img: "p6Rejuvabeads", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuvabeads" },
  ],
  productsNotSure: "nie wiesz, które są dla ciebie odpowiednie? właśnie po to jest quiz.",
  productsCta: "znajdź swoją rutynę włosową",
  storyEyebrow: "moja historia włosowa",
  storyHeading: "to samo miejsce,",
  storyHighlight: "dużo więcej włosów",
  storyP1: "w lipcu 2025 patrzyłam na swoje włosy i jedyne słowa, jakie miałam dla nich, to",
  storyP1Bold: "nitkowate i przezroczyste",
  storyP1End: ". cienkie na skroniach, rzadkie na końcach.",
  storyP2: "szczerze mówiąc, byłam sceptyczna wobec wypróbowania monat. spróbowałam mimo to i zachowałam prostotę:",
  storyP2Bold: "olejek i serum obronne przeciw przerzedzaniu",
  storyP2End: ", każdego dnia. (moja rada: postaw serum obok szczoteczki do zębów, żebyś naprawdę go używała.)",
  storyP3: "nieco ponad rok później to samo miejsce z dużo większą ilością włosów. wiedziałam, że moje włosy się zmieniły. nie zdawałam sobie sprawy jak bardzo, dopóki nie zobaczyłam zdjęć obok siebie.",
  clientResults: "prawdziwe wyniki klientek",
  happyEyebrow: "zadowolone klientki",
  happyHeading: "moje wiadomości",
  happyHighlight: "ostatnio",
  happyNote: "prawdziwe wiadomości od klientek, udostępnione za zgodą.",
  sliderHint: "przesuń, aby zobaczyć więcej →",
  howItWorksHeading: "jak to działa",
  howItWorks: [
    { num: "1", title: "wypełnij quiz", desc: "powiedz mi, co dzieje się z twoimi włosami. zajmie to około minuty." },
    { num: "2", title: "tworzę twoją rutynę", desc: "osobiście wyślę ci produkty, których bym użyła na twoim miejscu, i dlaczego." },
    { num: "3", title: "dostawa pod drzwi", desc: "twoja rutyna jest wysyłana bezpośrednio do ciebie, gdziekolwiek mieszkasz." },
  ],
  howItWorksGuarantee: "30 dni gwarancji zwrotu pieniędzy, żebyś mogła wypróbować bez ryzyka.",
  whereWeShip: "dostępne w 18 krajach + rozszerzamy się",
  quizTitle: "znajdź swoją rutynę włosową",
  step1Title: "co dzieje się z twoimi włosami?",
  step1Subtitle: "zaznacz wszystko, co pasuje",
  step1Options: ["przerzedzanie lub wypadanie", "wypadanie poporodowe", "zmiany związane z menopauzą", "wypadanie po stresie lub chorobie", "puszące się lub niesforne", "suche lub zniszczone końcówki", "tłusta lub łuszcząca się skóra głowy", "wolny wzrost", "farbowane lub rozjaśniane", "cienkie lub płaskie", "kręcone lub teksturowane", "inne"],
  step2Title: "jak wygląda twoja obecna rutyna?",
  step2Subtitle: "zaznacz wszystko, co pasuje",
  step2Options: ["myję codziennie", "myję 2-3x w tygodniu", "myję raz w tygodniu lub rzadziej", "często używam narzędzi termicznych", "najczęściej suszę na powietrzu", "używam produktów z drogerii", "próbowałam wszystkiego", "właściwie nie mam rutyny"],
  step3Title: "prawie gotowe! gdzie wysłać twoją rutynę?",
  step3Subtitle: "osobiście wyślę ci moje rekomendacje",
  firstNameLabel: "imię",
  firstNamePlaceholder: "twoje imię",
  contactMethodLabel: "jak się z tobą skontaktować?",
  instagramOption: "dm na instagramie",
  textOption: "sms",
  igHandlePlaceholder: "@twojnick",
  countryLabel: "kraj",
  countryPlaceholder: "wybierz swój kraj",
  phoneLabel: "numer telefonu",
  phonePlaceholder: "+48 512 345 678",
  submitButton: "wyślij moją rutynę",
  submitting: "wysyłanie...",
  thankYouTitle: "jesteś zapisana!",
  thankYouMessage: "czytam każdą wiadomość osobiście i napiszę do ciebie w ciągu 24 godzin z rutyną, którą bym użyła dla twoich włosów.",
  thankYouFollowLine: "jeśli wybrałaś instagram, obserwuj mnie, żeby moja wiadomość nie zginęła w twoich prośbach 👇",
  thankYouFollowBtn: "obserwuj @iamkristenhinman",
  footerNew: "nowość",
  footerEarn: "zarabiaj dodatkowe pieniądze ze swojego telefonu",
  footerShare: ", po prostu dzieląc się produktami, które kochasz. nasz program partnerski właśnie wystartował na całym świecie na",
  footerTiktok: "tiktok",
  footerPlus: "+",
  footerMeta: "meta",
  footerRunIt: "chcesz lepszą zniżkę na produkty i dostęp do naszego programu partnerskiego? zacznij od mojego programu RUN IT. napisz do mnie na instagramie, żeby dowiedzieć się, jak to działa.",
  footerRunItBtn: "zobacz jak działa RUN IT",
  footerDmBtn: "napisz do mnie na instagramie",
  available: "dostępne w 18 krajach i rozszerzamy się",
  follow: "obserwuj mnie na instagramie @iamkristenhinman",
  errors: { firstName: "wpisz swoje imię", contact: "wpisz dane kontaktowe", country: "wybierz swój kraj", phone: "wpisz swój numer telefonu" }
};

// Dutch content
const CONTENT_NL = {
  pageTitle: "vind jouw haarverzorgingsroutine · kristen hinman",
  headline: "moderne natuur voor",
  headlineHighlight: "jouw haar",
  subline: "functionele geneeskunde voor je hoofdhuid. plantaardige, klinisch geteste luxe haarverzorging, geliefd bij meer dan 3 miljoen klanten wereldwijd. beantwoord 3 snelle vragen en ik stel persoonlijk jouw haarverzorgingsroutine samen.",
  beforeAfter: "VOOR · NA",
  before: "voor",
  after: "na",
  whatIsMonatEyebrow: "wat is monat",
  whatIsMonatHeading: "luxe haarverzorging die",
  whatIsMonatHighlight: "begint bij de hoofdhuid",
  whatIsMonatBody: "monat is een familiebedrijf in haarverzorging, al 12 jaar sterk, gebouwd op moderne natuur: plantaardige ingrediënten en klinisch geteste formules die je hoofdhuid en haar vanaf de wortel voeden. geen agressieve sulfaten of parabenen. elke formule voldoet aan EU-normen.",
  stats: ["3m+ klanten wereldwijd", "12 jaar", "EU-normen", "18 landen"],
  womenLoveIt: "vrouwen zijn er dol op voor",
  tags: ["minder haaruitval", "meer volume", "minder pluizen", "postpartum haar", "menopauze haar", "haaruitval na ziekte of stress", "sterkere punten", "kalmere hoofdhuid", "+ meer"],
  bestSellersEyebrow: "onze bestsellers",
  bestSellersHeading: "de producten waar",
  bestSellersHighlight: "iedereen naar vraagt",
  shopNow: "koop nu",
  products: [
    { emoji: "🧴", name: "hair thinning defense serum", desc: "mijn \"extensions in een flesje.\" een dagelijks hoofdhuidserum dat klinisch bewezen haaruitval met 92% vermindert terwijl het groei + volume stimuleert. op waterbasis, nooit vettig.", tip: "mijn tip: zet het naast je tandenborstel zodat je het echt elke dag gebruikt.", img: "p1Serum", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=ir-clinical-hair-thinning-defense" },
    { emoji: "💧", name: "rejuveniqe oil", desc: "degene die ik nooit zou opgeven. 13 plantoliën, antioxidanten + voedingsstoffen die vocht, glans en zachtheid terugbrengen. een paar druppels op je punten of als hoofdhuidbehandeling. vegan + cruelty free.", tip: "", img: "p2Oil", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuveniqe-oil-intensive" },
    { emoji: "🧜", name: "rejuvabeads", desc: "mijn gespleten punten redder. het repareert gespleten punten in 60 seconden zodat ze stoppen met afbreken en je je lengte behoudt. haar laten groeien en lengte behouden zijn twee verschillende dingen, en dit is het behouden gedeelte.", tip: "", img: "p6Rejuvabeads", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuvabeads" },
  ],
  productsNotSure: "niet zeker welke geschikt zijn voor jou? daar is de quiz precies voor.",
  productsCta: "vind jouw haarverzorgingsroutine",
  storyEyebrow: "mijn haarverhaal",
  storyHeading: "dezelfde plek,",
  storyHighlight: "veel meer haar",
  storyP1: "in juli 2025 keek ik naar mijn haar en de enige woorden die ik ervoor had waren",
  storyP1Bold: "sliertig en doorzichtig",
  storyP1End: ". dun bij mijn slapen, schaars aan de punten.",
  storyP2: "eerlijk gezegd was ik sceptisch over monat proberen. ik probeerde het toch, en hield het simpel:",
  storyP2Bold: "een olie en een haaruitval defense serum",
  storyP2End: ", elke dag. (mijn tip: zet het serum naast je tandenborstel zodat je het echt gebruikt.)",
  storyP3: "iets meer dan een jaar later is het dezelfde plek met veel meer haar. ik wist dat mijn haar was veranderd. ik realiseerde me niet hoeveel tot ik de foto's naast elkaar zag.",
  clientResults: "echte klantresultaten",
  happyEyebrow: "tevreden klanten",
  happyHeading: "mijn DMs",
  happyHighlight: "laatst",
  happyNote: "echte berichten van klanten, gedeeld met toestemming.",
  sliderHint: "veeg voor meer →",
  howItWorksHeading: "hoe het werkt",
  howItWorks: [
    { num: "1", title: "doe de quiz", desc: "vertel me wat er met je haar aan de hand is. het duurt ongeveer een minuut." },
    { num: "2", title: "ik stel je routine samen", desc: "ik stuur je persoonlijk de producten die ik zou gebruiken als ik jou was, en waarom." },
    { num: "3", title: "bezorgd aan je deur", desc: "je routine wordt rechtstreeks naar jou verzonden, waar je ook woont." },
  ],
  howItWorksGuarantee: "30 dagen niet-goed-geld-terug garantie, zodat je risicovrij kunt proberen.",
  whereWeShip: "beschikbaar in 18 landen + groeiende",
  quizTitle: "vind jouw haarverzorgingsroutine",
  step1Title: "wat is er aan de hand met je haar?",
  step1Subtitle: "selecteer alles wat van toepassing is",
  step1Options: ["dunner worden of uitvallen", "postpartum haaruitval", "menopauze-gerelateerde veranderingen", "haaruitval door stress of ziekte", "pluizig of onhandelbaar", "droge of beschadigde punten", "vette of schilferige hoofdhuid", "langzame groei", "geverfd of gebleekt", "fijn of plat", "krullend of getextureerd", "anders"],
  step2Title: "hoe ziet je huidige routine eruit?",
  step2Subtitle: "selecteer alles wat van toepassing is",
  step2Options: ["ik was dagelijks", "ik was 2-3x per week", "ik was eens per week of minder", "ik gebruik vaak warmte tools", "ik laat meestal luchtdrogen", "ik gebruik drogisterij producten", "ik heb alles geprobeerd", "ik heb eigenlijk geen routine"],
  step3Title: "bijna klaar! waar stuur ik je routine naartoe?",
  step3Subtitle: "ik stuur je persoonlijk mijn aanbevelingen",
  firstNameLabel: "voornaam",
  firstNamePlaceholder: "jouw naam",
  contactMethodLabel: "hoe kan ik je bereiken?",
  instagramOption: "instagram dm",
  textOption: "sms",
  igHandlePlaceholder: "@jouwnaam",
  countryLabel: "land",
  countryPlaceholder: "selecteer je land",
  phoneLabel: "mobiel nummer",
  phonePlaceholder: "+31 6 12345678",
  submitButton: "stuur mijn routine",
  submitting: "verzenden...",
  thankYouTitle: "je bent binnen!",
  thankYouMessage: "ik lees elke aanvraag zelf en stuur je binnen 24 uur de routine die ik zou gebruiken voor jouw haar.",
  thankYouFollowLine: "als je instagram hebt gekozen, volg me zodat mijn bericht niet verloren gaat in je verzoeken 👇",
  thankYouFollowBtn: "volg @iamkristenhinman",
  footerNew: "nieuw",
  footerEarn: "verdien extra geld vanaf je telefoon",
  footerShare: ", simpelweg door producten te delen waar je van houdt. ons affiliate programma is net wereldwijd gelanceerd op",
  footerTiktok: "tiktok",
  footerPlus: "+",
  footerMeta: "meta",
  footerRunIt: "wil je betere korting op je producten en toegang tot ons affiliate programma? begin met mijn RUN IT programma. DM me op instagram om te weten hoe het werkt.",
  footerRunItBtn: "zie hoe RUN IT werkt",
  footerDmBtn: "DM me op instagram",
  available: "beschikbaar in 18 landen en groeiende",
  follow: "volg me op instagram @iamkristenhinman",
  errors: { firstName: "vul je naam in", contact: "vul je contactgegevens in", country: "selecteer je land", phone: "vul je telefoonnummer in" }
};

// Lithuanian content
const CONTENT_LT = {
  pageTitle: "rask savo plaukų priežiūros rutiną · kristen hinman",
  headline: "moderni gamta tavo",
  headlineHighlight: "plaukams",
  subline: "funkcinė medicina tavo galvos odai. augalinė, kliniškai ištirta prabangios plaukų priežiūros linija, mylima daugiau nei 3 milijonų klientių visame pasaulyje. atsakyk į 3 greitus klausimus ir aš asmeniškai sukursiu tau plaukų priežiūros rutiną.",
  beforeAfter: "PRIEŠ · PO",
  before: "prieš",
  after: "po",
  whatIsMonatEyebrow: "kas yra monat",
  whatIsMonatHeading: "prabangios plaukų priežiūros produktai, kurie",
  whatIsMonatHighlight: "prasideda nuo galvos odos",
  whatIsMonatBody: "monat yra šeimos plaukų priežiūros prekės ženklas, stiprus jau 12 metų, sukurtas remiantis modernia gamta: augaliniai ingredientai ir kliniškai išbandytos formulės, kurios maitina galvos odą ir plaukus nuo pat šaknies. be agresyvių sulfatų ar parabenų. kiekviena formulė atitinka ES standartus.",
  stats: ["3m+ klientų visame pasaulyje", "12 metų", "ES standartai", "18 šalių"],
  womenLoveIt: "moterys tai myli už",
  tags: ["mažiau slinkimo", "daugiau tankumo", "mažiau pūkavimo", "plaukai po gimdymo", "menopauzės plaukai", "slinkimas po ligos ar streso", "stipresni galiukai", "rami galvos oda", "+ daugiau"],
  bestSellersEyebrow: "mūsų bestseleriai",
  bestSellersHeading: "produktai, apie kuriuos",
  bestSellersHighlight: "visi manęs klausia",
  shopNow: "pirkti dabar",
  products: [
    { emoji: "🧴", name: "hair thinning defense serum", desc: "mano \"priauginiai buteliuke.\" kasdieninis galvos odos serumas, kliniškai įrodyta, kad sumažina plaukų slinkimą 92%, tuo pačiu skatindamas augimą + tankumą. vandens pagrindo, niekada neriebina.", tip: "mano patarimas: laikyk jį šalia dantų šepetėlio, kad tikrai naudotum kiekvieną dieną.", img: "p1Serum", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=ir-clinical-hair-thinning-defense" },
    { emoji: "💧", name: "rejuveniqe oil", desc: "tas, kurio niekada neatsisakyčiau. 13 augalinių aliejų, antioksidantų + maistingųjų medžiagų, kurios grąžina drėgmę, blizgesį ir minkštumą. keli lašai ant galiukų arba kaip galvos odos procedūra. veganiškas + be žiaurumo gyvūnams.", tip: "", img: "p2Oil", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuveniqe-oil-intensive" },
    { emoji: "🧜", name: "rejuvabeads", desc: "mano skilusiųgaliukų gelbėtojas. sutvarko skilusius galiukus per 60 sekundžių, kad jie nustotų lūžinėti ir išlaikytum savo ilgį. plaukų auginimas ir ilgio išlaikymas yra du skirtingi dalykai, ir tai yra išlaikymo dalis.", tip: "", img: "p6Rejuvabeads", link: "https://monatsocialshop.com/KRISTEN-HINMAN?q=rejuvabeads" },
  ],
  productsNotSure: "nežinai, kurie tau tinka? būtent tam ir yra testas.",
  productsCta: "rask savo plaukų priežiūros rutiną",
  storyEyebrow: "mano plaukų istorija",
  storyHeading: "ta pati vieta,",
  storyHighlight: "daug daugiau plaukų",
  storyP1: "2025 m. liepą žiūrėjau į savo plaukus ir vieninteliai žodžiai, kuriuos turėjau jiems apibūdinti, buvo",
  storyP1Bold: "siūliški ir permatomi",
  storyP1End: ". ploni prie smilkinių, reti galiukuose.",
  storyP2: "atvirai kalbant, buvau skeptiška dėl monat išbandymo. vis tiek išbandžiau ir laikiau paprastai:",
  storyP2Bold: "aliejus ir plaukų slinkimo gynybinis serumas",
  storyP2End: ", kiekvieną dieną. (mano patarimas: laikyk serumą šalia dantų šepetėlio, kad tikrai naudotum.)",
  storyP3: "praėjus kiek daugiau nei metams, tai ta pati vieta su daug daugiau plaukų. žinojau, kad mano plaukai pasikeitė. nesupratau kiek, kol nepamačiau nuotraukų vieną šalia kitos.",
  clientResults: "tikri klientų rezultatai",
  happyEyebrow: "patenkinti klientai",
  happyHeading: "mano žinutės",
  happyHighlight: "pastaruoju metu",
  happyNote: "tikros klientų žinutės, pasidalintos su leidimu.",
  sliderHint: "braukite, kad pamatytumėte daugiau →",
  howItWorksHeading: "kaip tai veikia",
  howItWorks: [
    { num: "1", title: "atlik testą", desc: "papasakok, kas vyksta su tavo plaukais. užtruks apie minutę." },
    { num: "2", title: "sukuriu tavo rutiną", desc: "asmeniškai parašysiu tau produktus, kuriuos naudočiau tavo vietoje, ir kodėl." },
    { num: "3", title: "pristatoma iki durų", desc: "tavo rutina siunčiama tiesiai tau, kad ir kur gyventum." },
  ],
  howItWorksGuarantee: "30 dienų pinigų grąžinimo garantija, kad galėtum išbandyti be rizikos.",
  whereWeShip: "prieinama 18 šalių + augame",
  quizTitle: "rask savo plaukų priežiūros rutiną",
  step1Title: "kas vyksta su tavo plaukais?",
  step1Subtitle: "pasirink viską, kas tinka",
  step1Options: ["retėjimas ar slinkimas", "plaukų slinkimas po gimdymo", "menopauzės susiję pokyčiai", "slinkimas dėl streso ar ligos", "pūkuoti ar nesuvaldomi", "sausi ar pažeisti galiukai", "riebi ar pleiskanota galvos oda", "lėtas augimas", "dažyti ar balinami", "ploni ar plokšti", "garbanoti ar tekstūruoti", "kita"],
  step2Title: "kaip atrodo tavo dabartinė rutina?",
  step2Subtitle: "pasirink viską, kas tinka",
  step2Options: ["plaunu kasdien", "plaunu 2-3x per savaitę", "plaunu kartą per savaitę ar rečiau", "dažnai naudoju šiluminius įrankius", "dažniausiai džiovinu natūraliai", "naudoju vaistinių produktus", "viską jau bandžiau", "iš tikrųjų neturiu rutinos"],
  step3Title: "beveik baigta! kur siųsti tavo rutiną?",
  step3Subtitle: "asmeniškai parašysiu tau savo rekomendacijas",
  firstNameLabel: "vardas",
  firstNamePlaceholder: "tavo vardas",
  contactMethodLabel: "kaip su tavimi susisiekti?",
  instagramOption: "instagram dm",
  textOption: "sms",
  igHandlePlaceholder: "@tavovardas",
  countryLabel: "šalis",
  countryPlaceholder: "pasirink savo šalį",
  phoneLabel: "mobiliojo telefono numeris",
  phonePlaceholder: "+370 612 34567",
  submitButton: "siųsti mano rutiną",
  submitting: "siunčiama...",
  thankYouTitle: "tu prisijungei!",
  thankYouMessage: "kiekvieną žinutę skaitau asmeniškai ir parašysiu tau per 24 valandas su rutina, kurią naudočiau tavo plaukams.",
  thankYouFollowLine: "jei pasirinkote instagram, sekite mane, kad mano žinutė nepasimestų jūsų prašymuose 👇",
  thankYouFollowBtn: "sekti @iamkristenhinman",
  footerNew: "naujiena",
  footerEarn: "užsidirbk papildomų pinigų iš savo telefono",
  footerShare: ", tiesiog dalindamasi produktais, kuriuos myli. mūsų partnerių programa ką tik startuota visame pasaulyje",
  footerTiktok: "tiktok",
  footerPlus: "+",
  footerMeta: "meta",
  footerRunIt: "nori geresnės nuolaidos savo produktams ir prieigos prie mūsų partnerių programos? pradėk nuo mano RUN IT programos. parašyk man instagram, kad sužinotum, kaip tai veikia.",
  footerRunItBtn: "pažiūrėk kaip veikia RUN IT",
  footerDmBtn: "parašyk man instagram",
  available: "prieinama 18 šalių ir augame",
  follow: "sek mane instagram @iamkristenhinman",
  errors: { firstName: "įvesk savo vardą", contact: "įvesk kontaktinius duomenis", country: "pasirink savo šalį", phone: "įvesk savo telefono numerį" }
};

// Countries list
const COUNTRIES = [
  "United States", "Canada", "United Kingdom", "Ireland", "Australia", 
  "New Zealand", "Spain", "Germany", "France", "Poland", "Netherlands",
  "Austria", "Belgium", "Denmark", "Sweden", "Norway", "Finland",
  "Czech Republic", "Lithuania", "Italy", "Other"
];

export default function TryProductsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quizRef = useRef(null);
  const dmScrollRef = useRef(null);
  const productScrollRef = useRef(null);
  
  // Language state
  const [lang, setLang] = useState(() => {
    const urlLang = searchParams.get('lang');
    if (['es', 'fr', 'de', 'it', 'pl', 'nl', 'lt'].includes(urlLang)) return urlLang;
    const savedLang = localStorage.getItem('hairQuizLang');
    return savedLang || 'en';
  });
  
  const [showLangMenu, setShowLangMenu] = useState(false);
  
  // Get content based on language
  const getContent = (langCode) => {
    switch(langCode) {
      case 'es': return CONTENT_ES;
      case 'fr': return CONTENT_FR;
      case 'de': return CONTENT_DE;
      case 'it': return CONTENT_IT;
      case 'pl': return CONTENT_PL;
      case 'nl': return CONTENT_NL;
      case 'lt': return CONTENT_LT;
      default: return CONTENT_EN;
    }
  };
  
  const t = getContent(lang);
  
  // Quiz state
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    hairConcerns: [],
    hairRoutine: [],
    firstName: "",
    contact: "",
    contactMethod: "instagram",
    country: "",
    phone: ""
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    document.title = t.pageTitle;
  }, [lang, t.pageTitle]);

  useEffect(() => {
    localStorage.setItem('hairQuizLang', lang);
  }, [lang]);

  useEffect(() => {
    window.scrollTo(0, 0);
    trackEvent(FunnelEvents.TRY_PRODUCTS_PAGE_VIEW);
  }, []);

  useEffect(() => {
    if (step > 0 && quizRef.current) {
      quizRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [step]);

  const selectLang = (langCode) => {
    setLang(langCode);
    setShowLangMenu(false);
  };

  const toggleConcern = (index) => {
    const englishLabel = CONTENT_EN.step1Options[index];
    setFormData(prev => ({
      ...prev,
      hairConcerns: prev.hairConcerns.includes(englishLabel)
        ? prev.hairConcerns.filter(c => c !== englishLabel)
        : [...prev.hairConcerns, englishLabel]
    }));
  };

  const toggleRoutine = (index) => {
    const englishLabel = CONTENT_EN.step2Options[index];
    setFormData(prev => ({
      ...prev,
      hairRoutine: prev.hairRoutine.includes(englishLabel)
        ? prev.hairRoutine.filter(r => r !== englishLabel)
        : [...prev.hairRoutine, englishLabel]
    }));
  };

  const scrollToQuiz = () => {
    setStep(1);
    trackEvent(FunnelEvents.TRY_PRODUCTS_STEP_1);
  };

  const goToStep2 = () => {
    setStep(2);
    trackEvent(FunnelEvents.TRY_PRODUCTS_STEP_2);
  };

  const goToStep3 = () => {
    setStep(3);
    trackEvent(FunnelEvents.TRY_PRODUCTS_STEP_3);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = t.errors.firstName;
    if (!formData.contact.trim()) newErrors.contact = t.errors.contact;
    if (!formData.country) newErrors.country = t.errors.country;
    if (!formData.phone.trim()) newErrors.phone = t.errors.phone;
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/product-inquiry`, {
        firstName: formData.firstName,
        contact: formData.contact,
        contactMethod: formData.contactMethod,
        country: formData.country,
        phone: formData.phone,
        hairConcerns: formData.hairConcerns,
        hairRoutine: formData.hairRoutine,
        language: lang.toUpperCase()
      });
      
      trackEvent(FunnelEvents.TRY_PRODUCTS_FORM_SUBMIT);
      trackStandardEvent('CompleteRegistration', { content_name: 'Product Inquiry' });
      
      setIsComplete(true);
    } catch (err) {
      console.error(err);
      setIsComplete(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isConcernSelected = (index) => {
    const englishLabel = CONTENT_EN.step1Options[index];
    return formData.hairConcerns.includes(englishLabel);
  };

  const isRoutineSelected = (index) => {
    const englishLabel = CONTENT_EN.step2Options[index];
    return formData.hairRoutine.includes(englishLabel);
  };

  return (
    <div className="min-h-screen bg-ink font-hanken">
      {/* Hero Section */}
      <section className="px-5 pt-12 pb-10 md:pt-16 md:pb-16">
        <div className="max-w-6xl mx-auto">
          {/* Language Selector - Backlit with glow */}
          <div className="flex justify-center mb-6 relative">
            {/* Glow effect behind button */}
            <div className="absolute inset-0 flex justify-center">
              <div className="w-48 h-16 bg-lime/40 rounded-full blur-xl"></div>
            </div>
            
            <div className="relative">
              <button
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="px-8 py-4 rounded-full bg-lime text-ink-deep font-hanken font-bold text-lg hover:bg-lime/90 transition-all shadow-[0_0_30px_rgba(212,242,75,0.5)] border-2 border-lime/50"
                data-testid="language-toggle"
              >
                {LANGUAGES.find(l => l.code === lang)?.flag ? (
                  <span className="mr-2">{LANGUAGES.find(l => l.code === lang)?.flag}</span>
                ) : (
                  <Globe className="inline-block w-5 h-5 mr-2" />
                )}
                {LANGUAGES.find(l => l.code === lang)?.nativeLabel || 'English'}
                <ChevronRight className={`inline-block w-5 h-5 ml-2 transition-transform ${showLangMenu ? 'rotate-90' : ''}`} />
              </button>
              
              {/* Language dropdown menu */}
              <AnimatePresence>
                {showLangMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-ink-deep border border-lime/30 rounded-xl overflow-hidden shadow-[0_0_40px_rgba(212,242,75,0.3)] z-50"
                  >
                    {LANGUAGES.map((language) => (
                      <button
                        key={language.code}
                        onClick={() => selectLang(language.code)}
                        className={`block w-full px-8 py-3 text-left font-hanken font-medium transition-all ${
                          lang === language.code 
                            ? 'bg-lime text-ink-deep' 
                            : 'text-oat hover:bg-lime/20'
                        }`}
                      >
                        {language.flag && <span className="mr-2">{language.flag}</span>}
                        {language.nativeLabel}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="flex justify-center mb-8">
            <span className="px-3 py-1 text-xs uppercase tracking-widest text-hairline border border-hairline/30 rounded-full">
              MONAT × KRISTEN HINMAN
            </span>
          </div>

          <h1 className="text-center mb-6">
            <span className="block font-anton text-4xl md:text-6xl lg:text-7xl text-oat lowercase tracking-tight">
              {t.headline}
            </span>
            <span className="block font-anton text-4xl md:text-6xl lg:text-7xl text-lime lowercase tracking-tight">
              {t.headlineHighlight}
            </span>
          </h1>

          <p className="text-center text-hairline text-base md:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            {t.subline}
          </p>

          {/* Hero Before/After */}
          <div className="relative max-w-xl mx-auto mb-8">
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <img src={IMAGES.heroBefore} alt="Before" className="w-full aspect-[3/4] object-cover rounded-lg" />
                <span className="absolute bottom-3 right-3 px-3 py-1 bg-lime text-ink-deep text-xs font-bold uppercase tracking-wider rounded">
                  {t.before}
                </span>
              </div>
              <div className="relative">
                <img src={IMAGES.heroAfter} alt="After" className="w-full aspect-[3/4] object-cover rounded-lg" />
                <span className="absolute bottom-3 right-3 px-3 py-1 bg-lime text-ink-deep text-xs font-bold uppercase tracking-wider rounded">
                  {t.after}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <motion.button
              onClick={scrollToQuiz}
              className="px-10 py-5 bg-lime text-ink-deep font-hanken font-bold text-lg rounded-full hover:bg-lime/90 transition-all lowercase shadow-lg"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="start-quiz-button"
            >
              {t.quizTitle}
              <ChevronRight className="inline-block w-6 h-6 ml-2" />
            </motion.button>
          </div>
        </div>
      </section>

      {/* What is MONAT Section - FIXED TEXT COLORS */}
      <section className="px-5 py-12 md:py-18 bg-oat">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-widest text-moss mb-4">{t.whatIsMonatEyebrow}</p>
          <h2 className="font-anton text-3xl md:text-4xl text-ink lowercase mb-6">
            {t.whatIsMonatHeading} <span className="text-lime bg-ink px-2">{t.whatIsMonatHighlight}</span>
          </h2>
          <p className="text-ink text-base md:text-lg leading-relaxed mb-8 max-w-3xl mx-auto">
            {t.whatIsMonatBody}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {t.stats.map((stat, i) => (
              <div key={i} className="px-4 py-3 border border-ink/20 rounded-lg">
                <span className="text-ink text-sm font-medium">{stat}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - After What is MONAT */}
      <section className="px-5 py-12 md:py-18 bg-oat border-t border-ink/10">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-anton text-2xl md:text-3xl text-ink text-center lowercase mb-10">
            {t.howItWorksHeading}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {t.howItWorks.map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="w-10 h-10 bg-lime rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-anton text-ink-deep text-lg">{item.num}</span>
                </div>
                <h3 className="font-hanken font-bold text-ink text-base mb-2 lowercase">
                  {i === 0 ? (
                    <button onClick={scrollToQuiz} className="hover:text-lime transition-colors underline decoration-lime underline-offset-2">
                      {item.title}
                    </button>
                  ) : (
                    item.title
                  )}
                </h3>
                <p className="text-ink text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-ink text-sm mb-6">{t.howItWorksGuarantee}</p>
          
          {/* Where We Ship */}
          <div className="mt-6 mb-8 text-center">
            <p className="text-moss-green text-xs uppercase tracking-wider mb-3">{t.whereWeShip}</p>
            <img 
              src={IMAGES.flags18} 
              alt="flags of the 18 countries where monat ships: united states, canada, mexico, panama, dominican republic, united kingdom, ireland, france, belgium, germany, austria, switzerland, spain, italy, poland, lithuania, australia, new zealand"
              className="mx-auto w-full max-w-[520px] h-auto"
            />
          </div>
          
          <div className="flex justify-center">
            <motion.button
              onClick={scrollToQuiz}
              className="px-10 py-5 bg-lime text-ink-deep font-hanken font-bold text-lg rounded-full hover:bg-lime/90 transition-all lowercase shadow-lg"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t.quizTitle} <ArrowRight className="inline-block w-5 h-5 ml-2" />
            </motion.button>
          </div>
        </div>
      </section>

      {/* Women Love It For - DARK BACKGROUND */}
      <section className="px-5 py-12 md:py-18 bg-ink">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-hairline/70 text-sm mb-4 lowercase">{t.womenLoveIt}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {t.tags.map((tag, i) => (
              <span key={i} className="px-3 py-1.5 text-xs text-oat border border-hairline/30 rounded-full lowercase">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Best Sellers Section - FIXED TEXT COLORS */}
      <section className="px-5 py-12 md:py-18 bg-oat">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-moss text-center mb-4">{t.bestSellersEyebrow}</p>
          <h2 className="font-anton text-3xl md:text-4xl text-ink text-center lowercase mb-10">
            {t.bestSellersHeading} <span className="text-lime bg-ink px-2">{t.bestSellersHighlight}</span>
          </h2>
          
          {/* Scrollable on mobile, grid on desktop - with slider hint */}
          <div className="relative">
            {/* Slider hint for mobile */}
            <p className="text-center text-ink/60 text-sm mb-3 md:hidden flex items-center justify-center gap-2">
              <ChevronLeft className="w-4 h-4" />
              {t.sliderHint || 'swipe to see more →'}
              <ChevronRight className="w-4 h-4" />
            </p>
            
            <div 
              ref={productScrollRef}
              className="flex md:grid md:grid-cols-3 gap-4 overflow-x-auto pb-4 md:pb-0 snap-x snap-mandatory md:snap-none scrollbar-hide" 
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {t.products.map((product, i) => (
                <div key={i} className="flex-shrink-0 w-80 md:w-auto snap-start bg-white rounded-xl p-6 shadow-sm flex flex-col">
                  <a href={product.link} target="_blank" rel="noopener noreferrer" className="block hover:opacity-90 transition-opacity">
                    <img src={IMAGES[product.img]} alt={product.name} className="w-full h-48 object-contain mb-4" />
                  </a>
                  <h3 className="font-hanken font-bold text-ink text-base mb-3 lowercase">
                    {product.emoji} {product.name}
                  </h3>
                  <p className="text-ink text-sm leading-relaxed mb-2">{product.desc}</p>
                  {product.tip && (
                    <p className="text-ink/80 text-sm italic mb-4">{product.tip}</p>
                  )}
                  <div className="mt-auto pt-4">
                    <a 
                      href={product.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block w-full py-3 bg-lime text-ink-deep font-hanken font-bold text-sm text-center rounded-full hover:bg-lime/90 transition-all uppercase tracking-wide"
                    >
                      {t.shopNow}
                    </a>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Scroll indicator dots for mobile */}
            <div className="flex justify-center gap-2 mt-4 md:hidden">
              {t.products.map((_, i) => (
                <div key={i} className="w-2 h-2 rounded-full bg-ink/30"></div>
              ))}
            </div>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-ink text-base mb-4">{t.productsNotSure}</p>
            <motion.button
              onClick={scrollToQuiz}
              className="px-10 py-5 bg-lime text-ink-deep font-hanken font-bold text-lg rounded-full hover:bg-lime/90 transition-all lowercase shadow-lg"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t.productsCta} <ArrowRight className="inline-block w-5 h-5 ml-2" />
            </motion.button>
          </div>
        </div>
      </section>

      {/* My Hair Story Section - DARK BACKGROUND */}
      <section className="px-5 py-12 md:py-18 bg-ink-deep">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Photos */}
            <div className="grid grid-cols-2 gap-4">
              <div className="relative">
                <img src={IMAGES.storyJuly} alt="July 2025" className="w-full aspect-square object-cover rounded-lg" />
                <span className="absolute bottom-2 left-2 px-2 py-1 bg-ink/80 text-oat text-xs rounded">
                  {lang === 'es' ? 'julio 2025' : 'july 2025'}
                </span>
              </div>
              <div className="relative">
                <img src={IMAGES.storyToday} alt="Today" className="w-full aspect-square object-cover rounded-lg" />
                <span className="absolute bottom-2 left-2 px-2 py-1 bg-ink/80 text-oat text-xs rounded">
                  {lang === 'es' ? 'hoy' : 'today'}
                </span>
              </div>
            </div>
            
            {/* Text */}
            <div>
              <p className="text-xs uppercase tracking-widest text-hairline/70 mb-4">{t.storyEyebrow}</p>
              <h2 className="font-anton text-3xl md:text-4xl text-oat lowercase mb-6">
                {t.storyHeading} <span className="text-lime">{t.storyHighlight}</span>
              </h2>
              <p className="text-oat text-base leading-relaxed mb-4">
                {t.storyP1} <strong>{t.storyP1Bold}</strong>{t.storyP1End}
              </p>
              <p className="text-oat text-base leading-relaxed mb-4">
                {t.storyP2} <strong>{t.storyP2Bold}</strong>{t.storyP2End}
              </p>
              <p className="text-oat text-base leading-relaxed">
                {t.storyP3}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Client Results Grid - DARK BACKGROUND */}
      <section className="px-5 py-12 md:py-18 bg-ink">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-anton text-2xl md:text-3xl text-oat text-center mb-8 lowercase">
            {t.clientResults}
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="relative">
              <img src={IMAGES.clientThinBefore} alt="Before" className="w-full aspect-square object-cover rounded-lg" />
              <span className="absolute bottom-2 left-2 px-2 py-1 bg-ink/80 text-oat text-xs rounded uppercase">
                {lang === 'es' ? 'antes' : 'before'}
              </span>
            </div>
            <div className="relative">
              <img src={IMAGES.clientThinAfter} alt="After" className="w-full aspect-square object-cover rounded-lg" />
              <span className="absolute bottom-2 left-2 px-2 py-1 bg-ink/80 text-oat text-xs rounded uppercase">
                {lang === 'es' ? 'después' : 'after'}
              </span>
            </div>
            <div className="relative">
              <img src={IMAGES.clientCurlyBefore} alt="Before" className="w-full aspect-square object-cover rounded-lg" />
              <span className="absolute bottom-2 left-2 px-2 py-1 bg-ink/80 text-oat text-xs rounded uppercase">
                {lang === 'es' ? 'antes' : 'before'}
              </span>
            </div>
            <div className="relative">
              <img src={IMAGES.clientCurlyAfter} alt="After" className="w-full aspect-square object-cover rounded-lg" />
              <span className="absolute bottom-2 left-2 px-2 py-1 bg-ink/80 text-oat text-xs rounded uppercase">
                {lang === 'es' ? 'después' : 'after'}
              </span>
            </div>
            <div className="relative">
              <img src={IMAGES.clientSilverBefore} alt="Before" className="w-full aspect-square object-cover rounded-lg" />
              <span className="absolute bottom-2 left-2 px-2 py-1 bg-ink/80 text-oat text-xs rounded uppercase">
                {lang === 'es' ? 'antes' : 'before'}
              </span>
            </div>
            <div className="relative">
              <img src={IMAGES.clientSilverAfter} alt="After" className="w-full aspect-square object-cover rounded-lg" />
              <span className="absolute bottom-2 left-2 px-2 py-1 bg-ink/80 text-oat text-xs rounded uppercase">
                {lang === 'es' ? 'después' : 'after'}
              </span>
            </div>
            <div className="relative">
              <img src={IMAGES.clientFineBefore} alt="Before" className="w-full aspect-square object-cover rounded-lg" />
              <span className="absolute bottom-2 left-2 px-2 py-1 bg-ink/80 text-oat text-xs rounded uppercase">
                {lang === 'es' ? 'antes' : 'before'}
              </span>
            </div>
            <div className="relative">
              <img src={IMAGES.clientFineAfter} alt="After" className="w-full aspect-square object-cover rounded-lg" />
              <span className="absolute bottom-2 left-2 px-2 py-1 bg-ink/80 text-oat text-xs rounded uppercase">
                {lang === 'es' ? 'después' : 'after'}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Happy Customers - DM Screenshots - LIGHT BACKGROUND FIXED */}
      <section className="px-5 py-12 md:py-18 bg-oat">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-moss text-center mb-4">{t.happyEyebrow}</p>
          <h2 className="font-anton text-3xl md:text-4xl text-ink text-center lowercase mb-8">
            {t.happyHeading} <span className="text-lime bg-ink px-2">{t.happyHighlight}</span>
          </h2>
          
          {/* Scrollable DM row */}
          <div 
            ref={dmScrollRef}
            className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {[IMAGES.r1, IMAGES.r2, IMAGES.r3, IMAGES.r4, IMAGES.r5].map((img, i) => (
              <div key={i} className="flex-shrink-0 w-72 snap-start">
                <img 
                  src={img} 
                  alt={`Customer message ${i + 1}`} 
                  className="w-full rounded-xl shadow-lg"
                />
              </div>
            ))}
          </div>
          
          <p className="text-center text-ink text-sm mt-4">{t.happyNote}</p>
        </div>
      </section>

      {/* How It Works - LIGHT BACKGROUND FIXED */}
      <section className="px-5 py-12 md:py-18 bg-oat">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-anton text-2xl md:text-3xl text-ink text-center lowercase mb-10">
            {t.howItWorksHeading}
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {t.howItWorks.map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 text-center shadow-sm">
                <div className="w-10 h-10 bg-lime rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-anton text-ink-deep text-lg">{item.num}</span>
                </div>
                <h3 className="font-hanken font-bold text-ink text-base mb-2 lowercase">
                  {i === 0 ? (
                    <button onClick={scrollToQuiz} className="hover:text-lime transition-colors underline decoration-lime underline-offset-2">
                      {item.title}
                    </button>
                  ) : (
                    item.title
                  )}
                </h3>
                <p className="text-ink text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-ink text-sm mb-6">{t.howItWorksGuarantee}</p>
          
          {/* Where We Ship */}
          <div className="mt-6 mb-8 text-center">
            <p className="text-moss-green text-xs uppercase tracking-wider mb-3">{t.whereWeShip}</p>
            <img 
              src={IMAGES.flags18} 
              alt="flags of the 18 countries where monat ships: united states, canada, mexico, panama, dominican republic, united kingdom, ireland, france, belgium, germany, austria, switzerland, spain, italy, poland, lithuania, australia, new zealand"
              className="mx-auto w-full max-w-[520px] h-auto"
            />
          </div>
          
          <div className="flex justify-center">
            <motion.button
              onClick={scrollToQuiz}
              className="px-10 py-5 bg-lime text-ink-deep font-hanken font-bold text-lg rounded-full hover:bg-lime/90 transition-all lowercase shadow-lg"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t.quizTitle} <ArrowRight className="inline-block w-5 h-5 ml-2" />
            </motion.button>
          </div>
        </div>
      </section>

      {/* Quiz Section - DARK BACKGROUND, NORMAL PADDING */}
      <section ref={quizRef} className="px-5 py-12 md:py-18 bg-ink-deep">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="font-anton text-3xl md:text-4xl text-oat lowercase mb-2">
              {t.quizTitle}
            </h2>
            
            {!isComplete && (
              <div className="flex justify-center gap-2 mt-4">
                {[1, 2, 3].map((s) => (
                  <div 
                    key={s}
                    className={`h-1.5 w-12 rounded-full transition-all ${
                      s <= (step === 0 ? 1 : step) ? 'bg-lime' : 'bg-hairline/30'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          <AnimatePresence mode="wait">
            {(step === 0 || step === 1) && !isComplete && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="space-y-6"
              >
                <div className="text-center mb-6">
                  <h3 className="font-hanken text-xl text-oat font-medium lowercase">{t.step1Title}</h3>
                  <p className="text-hairline/70 text-sm mt-1 lowercase">{t.step1Subtitle}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {t.step1Options.map((option, i) => (
                    <button
                      key={i}
                      onClick={() => toggleConcern(i)}
                      className={`relative p-4 text-left rounded-lg border transition-all text-sm lowercase ${
                        isConcernSelected(i)
                          ? 'border-lime bg-lime/10 text-oat'
                          : 'border-hairline/30 text-hairline hover:border-hairline/60'
                      }`}
                      data-testid={`concern-${i}`}
                    >
                      {option}
                      {isConcernSelected(i) && (
                        <Check className="absolute top-2 right-2 w-4 h-4 text-lime" />
                      )}
                    </button>
                  ))}
                </div>

                <motion.button
                  onClick={goToStep2}
                  disabled={formData.hairConcerns.length === 0}
                  className="w-full py-4 bg-lime text-ink-deep font-hanken font-semibold rounded-full hover:bg-lime/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed lowercase"
                  whileHover={{ scale: formData.hairConcerns.length > 0 ? 1.01 : 1 }}
                  whileTap={{ scale: formData.hairConcerns.length > 0 ? 0.99 : 1 }}
                  data-testid="continue-step-1"
                >
                  {lang === 'es' ? 'continuar' : 'continue'}
                </motion.button>
              </motion.div>
            )}

            {step === 2 && !isComplete && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="space-y-6"
              >
                <div className="text-center mb-6">
                  <h3 className="font-hanken text-xl text-oat font-medium lowercase">{t.step2Title}</h3>
                  <p className="text-hairline/70 text-sm mt-1 lowercase">{t.step2Subtitle}</p>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {t.step2Options.map((option, i) => (
                    <button
                      key={i}
                      onClick={() => toggleRoutine(i)}
                      className={`relative p-4 text-left rounded-lg border transition-all text-sm lowercase ${
                        isRoutineSelected(i)
                          ? 'border-lime bg-lime/10 text-oat'
                          : 'border-hairline/30 text-hairline hover:border-hairline/60'
                      }`}
                      data-testid={`routine-${i}`}
                    >
                      {option}
                      {isRoutineSelected(i) && (
                        <Check className="absolute top-2 right-2 w-4 h-4 text-lime" />
                      )}
                    </button>
                  ))}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="px-6 py-4 border border-hairline/30 text-hairline rounded-full hover:bg-oat/5 transition-all lowercase"
                    data-testid="back-step-2"
                  >
                    {lang === 'es' ? 'atrás' : 'back'}
                  </button>
                  <motion.button
                    onClick={goToStep3}
                    disabled={formData.hairRoutine.length === 0}
                    className="flex-1 py-4 bg-lime text-ink-deep font-hanken font-semibold rounded-full hover:bg-lime/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed lowercase"
                    whileHover={{ scale: formData.hairRoutine.length > 0 ? 1.01 : 1 }}
                    whileTap={{ scale: formData.hairRoutine.length > 0 ? 0.99 : 1 }}
                    data-testid="continue-step-2"
                  >
                    {lang === 'es' ? 'continuar' : 'continue'}
                  </motion.button>
                </div>
              </motion.div>
            )}

            {step === 3 && !isComplete && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
              >
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="text-center mb-6">
                    <h3 className="font-hanken text-xl text-oat font-medium lowercase">{t.step3Title}</h3>
                    <p className="text-hairline/70 text-sm mt-1 lowercase">{t.step3Subtitle}</p>
                  </div>

                  {/* First Name */}
                  <div>
                    <label className="block text-hairline text-sm mb-2 lowercase">
                      {t.firstNameLabel} <span className="text-lime">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                      className={`w-full px-4 py-3 bg-ink border rounded-lg text-oat placeholder-hairline/50 focus:outline-none focus:border-lime transition-all ${
                        errors.firstName ? 'border-red-500' : 'border-hairline/30'
                      }`}
                      placeholder={t.firstNamePlaceholder}
                      data-testid="firstName-input"
                    />
                    {errors.firstName && <p className="text-red-400 text-xs mt-1">{errors.firstName}</p>}
                  </div>

                  {/* Contact Method Toggle */}
                  <div>
                    <label className="block text-hairline text-sm mb-2 lowercase">{t.contactMethodLabel}</label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, contactMethod: 'instagram', contact: ''})}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border transition-all lowercase ${
                          formData.contactMethod === 'instagram'
                            ? 'border-lime bg-lime/10 text-oat'
                            : 'border-hairline/30 text-hairline hover:border-hairline/60'
                        }`}
                        data-testid="contact-instagram"
                      >
                        <Instagram className="w-4 h-4" />
                        {t.instagramOption}
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({...formData, contactMethod: 'text', contact: ''})}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg border transition-all lowercase ${
                          formData.contactMethod === 'text'
                            ? 'border-lime bg-lime/10 text-oat'
                            : 'border-hairline/30 text-hairline hover:border-hairline/60'
                        }`}
                        data-testid="contact-text"
                      >
                        <Phone className="w-4 h-4" />
                        {t.textOption}
                      </button>
                    </div>
                  </div>

                  {/* Contact Input (IG or preferred) */}
                  <div>
                    <label className="block text-hairline text-sm mb-2 lowercase">
                      {formData.contactMethod === 'instagram' ? 'instagram' : (lang === 'es' ? 'contacto preferido' : 'preferred contact')} <span className="text-lime">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.contact}
                      onChange={(e) => setFormData({...formData, contact: e.target.value})}
                      className={`w-full px-4 py-3 bg-ink border rounded-lg text-oat placeholder-hairline/50 focus:outline-none focus:border-lime transition-all ${
                        errors.contact ? 'border-red-500' : 'border-hairline/30'
                      }`}
                      placeholder={formData.contactMethod === 'instagram' ? t.igHandlePlaceholder : t.phonePlaceholder}
                      data-testid="contact-input"
                    />
                    {errors.contact && <p className="text-red-400 text-xs mt-1">{errors.contact}</p>}
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-hairline text-sm mb-2 lowercase">
                      {t.countryLabel} <span className="text-lime">*</span>
                    </label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-hairline/50" />
                      <select
                        value={formData.country}
                        onChange={(e) => setFormData({...formData, country: e.target.value})}
                        className={`w-full pl-10 pr-4 py-3 bg-ink border rounded-lg text-oat focus:outline-none focus:border-lime transition-all appearance-none ${
                          errors.country ? 'border-red-500' : 'border-hairline/30'
                        } ${!formData.country ? 'text-hairline/50' : ''}`}
                        data-testid="country-select"
                      >
                        <option value="" disabled>{t.countryPlaceholder}</option>
                        {COUNTRIES.map((country) => (
                          <option key={country} value={country} className="bg-ink text-oat">
                            {country}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errors.country && <p className="text-red-400 text-xs mt-1">{errors.country}</p>}
                  </div>

                  {/* Phone - Always required */}
                  <div>
                    <label className="block text-hairline text-sm mb-2 lowercase">
                      {t.phoneLabel} <span className="text-lime">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-hairline/50" />
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className={`w-full pl-10 pr-4 py-3 bg-ink border rounded-lg text-oat placeholder-hairline/50 focus:outline-none focus:border-lime transition-all ${
                          errors.phone ? 'border-red-500' : 'border-hairline/30'
                        }`}
                        placeholder={t.phonePlaceholder}
                        data-testid="phone-input"
                      />
                    </div>
                    {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                  </div>

                  {/* Submit */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="px-6 py-4 border border-hairline/30 text-hairline rounded-full hover:bg-oat/5 transition-all lowercase"
                      data-testid="back-step-3"
                    >
                      {lang === 'es' ? 'atrás' : 'back'}
                    </button>
                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 py-4 bg-lime text-ink-deep font-hanken font-semibold rounded-full hover:bg-lime/90 transition-all disabled:opacity-50 lowercase"
                      whileHover={{ scale: !isSubmitting ? 1.01 : 1 }}
                      whileTap={{ scale: !isSubmitting ? 0.99 : 1 }}
                      data-testid="submit-button"
                    >
                      {isSubmitting ? t.submitting : t.submitButton}
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            )}

            {isComplete && (
              <motion.div
                key="complete"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-10"
              >
                <div className="w-16 h-16 bg-lime rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-ink-deep" />
                </div>
                <h3 className="font-anton text-3xl text-oat lowercase mb-3">{t.thankYouTitle}</h3>
                <p className="text-hairline leading-relaxed max-w-md mx-auto mb-4">{t.thankYouMessage}</p>
                <p className="text-hairline/80 text-sm leading-relaxed max-w-md mx-auto mb-6">{t.thankYouFollowLine}</p>
                <a
                  href="https://instagram.com/iamkristenhinman"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-lime text-ink-deep font-hanken font-bold px-6 py-3 rounded-full hover:bg-lime/90 transition-all"
                >
                  {t.thankYouFollowBtn}
                </a>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Footer - Backlit glow effect */}
      <footer className="relative px-5 py-16 bg-ink overflow-hidden">
        {/* Lime glow effect behind content */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[400px] bg-lime/20 rounded-full blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-xl mx-auto text-center">
          {/* Affiliate callout card */}
          <div className="bg-ink-deep/80 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-lime/20 shadow-[0_0_60px_-15px_rgba(212,242,75,0.3)]">
            <p className="text-oat text-lg mb-4 leading-relaxed">
              <span className="inline-block px-3 py-1 bg-lime text-ink-deep text-xs font-bold uppercase tracking-wider rounded-full mr-2 animate-pulse">
                {t.footerNew}
              </span>
              <strong className="text-lime">{t.footerEarn}</strong>
              {t.footerShare} <TikTokIcon /> {t.footerTiktok} {t.footerPlus} <MetaIcon /> {t.footerMeta}.
            </p>
            <p className="text-hairline text-sm mb-6">{t.footerRunIt}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a 
                href="https://kristenhinman.com/run-it"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-lime text-ink-deep font-hanken font-bold text-base rounded-full hover:bg-lime/90 hover:shadow-[0_0_30px_rgba(212,242,75,0.5)] transition-all lowercase"
              >
                {t.footerRunItBtn} <ArrowRight className="inline-block w-5 h-5 ml-1" />
              </a>
              <a 
                href="https://ig.me/m/iamkristenhinman"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 border border-lime/50 text-oat font-hanken font-medium text-base rounded-full hover:bg-lime/10 hover:border-lime transition-all lowercase"
              >
                {t.footerDmBtn}
              </a>
            </div>
          </div>

          {/* Where We Ship - Footer */}
          <div className="mb-4 text-center">
            <p className="text-lime/80 text-xs uppercase tracking-wider mb-3">{t.whereWeShip}</p>
            <img 
              src={IMAGES.flags18} 
              alt="flags of the 18 countries where monat ships: united states, canada, mexico, panama, dominican republic, united kingdom, ireland, france, belgium, germany, austria, switzerland, spain, italy, poland, lithuania, australia, new zealand"
              className="mx-auto w-full max-w-[520px] h-auto"
            />
          </div>
          
          <a 
            href="https://instagram.com/iamkristenhinman"
            target="_blank"
            rel="noopener noreferrer"
            className="text-hairline/70 text-sm mb-4 hover:text-lime transition-colors inline-flex items-center gap-1"
          >
            <Instagram className="w-4 h-4" />
            {t.follow}
          </a>
          <p className="text-hairline/50 text-xs">(541) 871-3311</p>
        </div>
      </footer>
    </div>
  );
}
